import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import Screens from "../../model/Screens";
import { MachineContext } from "../fsm/useMachine";

interface Props {
    item: Screens;
    index: number;
    isSelected: boolean;
    onSelect: (index: number) => void;
    onDelete: (index: number) => void;
    onCopy: (index: number) => void;
    onDragStart: (index: number) => void;
    onDragOver: (index: number) => void;
    onDragEnd: () => void;
    isDragging: boolean;
    dragOverIndex: number | null;
    thumbWidth: number;
    thumbHeight: number;
}

export const ScreenListItem: React.FC<Props> = ({
    item,
    index,
    isSelected,
    onSelect,
    onDelete,
    onCopy,
    onDragStart,
    onDragOver,
    onDragEnd,
    isDragging,
    dragOverIndex,
    thumbWidth,
    thumbHeight,
}) => {
    const [imgFailed, setImgFailed] = useState(false);

    // Compute a simple content fingerprint to detect when screen content changes
    const contentFingerprint = useMemo(() => {
        const bgColor = item.FlowScreenModelBackgroundColorKey?.asRgba?.() || '';
        const mmoCount = item.mixedMediaObjects?.length || 0;
        const mmoIds = (item.mixedMediaObjects || []).map((m: any) => m.id).join(',');
        return `${bgColor}-${mmoCount}-${mmoIds}`;
    }, [item.FlowScreenModelBackgroundColorKey, item.mixedMediaObjects]);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
            }
        };
        if (contextMenu) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [contextMenu]);

    // Reset img failed state and canvas when preview or content changes
    useEffect(() => {
        setImgFailed(false);
        setCanvasDataUrl(null); // Force canvas regeneration
    }, [item.previewScreenShot, contentFingerprint]);

    // Handle both tagged ({__tag, value}) and direct URL formats
    const getUrl = (asset: any): string | undefined => {
        if (!asset?.externalURL) return undefined;
        const ext = asset.externalURL;
        // Tagged format: {__tag: "string", value: "https://..."}
        if (ext.__tag === "string" && typeof ext.value === "string") return ext.value;
        // Tagged format: {__tag: "file", value: File} - can't use File objects
        if (ext.__tag === "file") return undefined;
        // Direct value
        if (typeof ext.value === "string") return ext.value;
        // Direct string
        if (typeof ext === "string") return ext;
        return undefined;
    };

    const previewUrl = getUrl(item.previewScreenShot);
    // Only use data URLs (from html2canvas) - external URLs cause CORS issues
    const hasValidPreview = previewUrl && previewUrl.startsWith('data:');

    // Generate canvas-based thumbnail when no valid preview exists
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null);

    const generateCanvasThumbnail = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        const viewW = 1024;
        const viewH = 768;
        const scale = thumbWidth / viewW;

        // Draw background
        const bgColor = item.FlowScreenModelBackgroundColorKey?.asRgba?.() || item.screenColor?.asRgba?.() || '#ffffff';
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, thumbWidth, thumbHeight);

        // Draw MMO hints (shapes, text blocks)
        const mmos = item.mixedMediaObjects || [];
        for (const mmo of mmos) {
            const f = mmo.frame || {};
            const x = (f.x || 0) * scale;
            const y = (f.y || 0) * scale;
            const w = Math.max((f.width || 50) * scale, 4);
            const h = Math.max((f.height || 30) * scale, 4);

            if (mmo.type === 'shape') {
                // Draw shape with its color
                const color = mmo.fillColor?.asRgba?.() || mmo.color?.asRgba?.() || 'rgba(0,0,0,0.1)';
                ctx.fillStyle = color;
                ctx.fillRect(x, y, w, h);
            } else if (mmo.type === 'text') {
                // Draw text hint
                ctx.fillStyle = 'rgba(128,128,128,0.3)';
                ctx.fillRect(x, y, w, h);
            } else if (mmo.type === 'image') {
                // Draw image placeholder
                ctx.fillStyle = 'rgba(200,200,200,0.5)';
                ctx.fillRect(x, y, w, h);
            }
        }

        setCanvasDataUrl(canvas.toDataURL('image/png'));
    }, [item, thumbWidth, thumbHeight]);

    // Generate canvas thumbnail when no preview or content changed
    useEffect(() => {
        // Always generate canvas thumbnail as fallback
        const timer = setTimeout(generateCanvasThumbnail, 50);
        return () => clearTimeout(timer);
    }, [contentFingerprint, generateCanvasThumbnail]);

    // Determine what to show
    const showCanvas = !hasValidPreview || imgFailed;
    const effectivePreviewUrl = canvasDataUrl || previewUrl;

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    const handleDelete = () => {
        setContextMenu(null);
        // Confirm before delete
        if (window.confirm("Delete this screen?")) {
            onDelete(index);
        }
    };

    const handleCopy = () => {
        setContextMenu(null);
        onCopy(index);
    };

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
        onDragStart(index);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver(index);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        onDragEnd();
    };

    const handleDragEnd = () => {
        onDragEnd();
    };

    const isDropTarget = dragOverIndex === index && isDragging;

    return (
        <>
            <div
                className={`screen-list-item ${isSelected ? "screen-list-item--selected" : ""} ${isDropTarget ? "screen-list-item--drop-target" : ""}`}
                draggable
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                onClick={() => onSelect(index)}
                onContextMenu={handleContextMenu}
                style={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 14,
                    padding: 6,
                    background: isSelected ? '#0070bc' : (isDropTarget ? 'rgba(0, 112, 188, 0.3)' : 'transparent'),
                    border: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease',
                    transform: isDropTarget ? 'scale(1.05)' : 'none',
                }}
            >
                {/* Hidden canvas for generating thumbnails */}
                <canvas
                    ref={canvasRef}
                    width={thumbWidth}
                    height={thumbHeight}
                    style={{ display: 'none' }}
                />
                <div
                    className="screen-list-item__thumbnail"
                    style={{ width: thumbWidth, height: thumbHeight, overflow: 'hidden', borderRadius: 10, flexShrink: 0 }}
                >
                    {/* Use html2canvas preview if available, fall back to canvas-generated thumbnail */}
                    {(hasValidPreview && !imgFailed) ? (
                        <img
                            src={previewUrl}
                            alt={`Screen ${index + 1}`}
                            draggable={false}
                            onError={() => setImgFailed(true)}
                            style={{
                                width: thumbWidth,
                                height: thumbHeight,
                                objectFit: 'cover',
                                display: 'block',
                                borderRadius: 10,
                            }}
                        />
                    ) : canvasDataUrl ? (
                        <img
                            src={canvasDataUrl}
                            alt={`Screen ${index + 1}`}
                            draggable={false}
                            style={{
                                width: thumbWidth,
                                height: thumbHeight,
                                objectFit: 'cover',
                                display: 'block',
                                borderRadius: 10,
                            }}
                        />
                    ) : (
                        <div
                            className="screen-list-item__placeholder"
                            style={{
                                width: thumbWidth,
                                height: thumbHeight,
                                backgroundColor: item.FlowScreenModelBackgroundColorKey?.asRgba?.() || item.screenColor?.asRgba?.() || "#e5e7eb",
                                borderRadius: 10,
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Mini content hints for screens without screenshots */}
                            {(item.mixedMediaObjects || []).slice(0, 4).map((mmo: any, i: number) => {
                                const f = mmo.frame || {};
                                const viewW = 1024;
                                const viewH = 768;
                                const scale = thumbWidth / viewW;
                                return (
                                    <div key={mmo.id || i} style={{
                                        position: 'absolute',
                                        left: (f.x || 0) * scale,
                                        top: (f.y || 0) * scale,
                                        width: Math.max((f.width || 100) * scale, 6),
                                        height: Math.max((f.height || 30) * scale, 4),
                                        backgroundColor: mmo.type === 'text' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)',
                                        borderRadius: 2,
                                    }} />
                                );
                            })}
                        </div>
                    )}
                </div>
                <span
                    className="screen-list-item__number"
                    style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: isSelected ? 'white' : '#333',
                        background: isSelected ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.85)',
                        padding: '1px 5px',
                        borderRadius: 3,
                        lineHeight: 1.2,
                    }}
                >
                    {index + 1}
                </span>
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <div
                    ref={contextMenuRef}
                    className="screen-context-menu"
                    style={{
                        position: "fixed",
                        left: contextMenu.x,
                        top: contextMenu.y,
                        zIndex: 9999,
                    }}
                >
                    <button onClick={handleCopy}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                        Duplicate
                    </button>
                    <button onClick={handleDelete} className="screen-context-menu__delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                        Delete
                    </button>
                </div>
            )}
        </>
    );
};

export default ScreenListItem;
